import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  AttachFile as AttachmentIcon,
  Upload as UploadIcon,
  Description as DocIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface Attachment {
  id: number;
  file_name: string;
  file_type: string;
  file_size: number;
  r2_url: string;
  description?: string;
  created_at: string;
}

interface Prescription {
  id: number;
  prescription_number: string;
  medication_name: string;
  dosage: string;
  quantity: number;
  status: string;
}

interface NotebookEntry {
  id: number;
  consultation_date: string;
  chief_complaint: string;
  symptoms_observed: string;
  physical_exam_findings: string;
  veterinarian_assessment: string;
  treatment_plan: string;
  follow_up_instructions: string;
  consultation_duration_minutes: number;
}

interface MedicalRecord {
  id: number;
  pet_id: number;
  pet_name: string;
  owner_name: string;
  veterinarian_name: string;
  appointment_id: number;
  appointment_date: string;
  visit_date: string;
  complaint: string;
  symptoms: string;
  diagnosis: string;
  physical_exam_findings: string;
  treatment_plan: string;
  prognosis: string;
  notes: string;
  status: string;
  prescriptions?: Prescription[];
  notebook_entry?: NotebookEntry;
}

const MedicalRecordDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [record, setRecord] = useState<MedicalRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [editForm, setEditForm] = useState({
    complaint: '',
    symptoms: '',
    diagnosis: '',
    physical_exam_findings: '',
    treatment_plan: '',
    prognosis: '',
    notes: '',
    status: '',
  });

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (id) fetchRecord();
  }, [id]);

  useEffect(() => {
    if (id && record) fetchAttachments();
  }, [id, record?.id]);

  const fetchAttachments = async () => {
    try {
      const res = await api.get(`${API_ENDPOINTS.UPLOADS.UPLOAD}?medical_record_id=${id}`);
      setAttachments((res.data || res || []) as Attachment[]);
    } catch {
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('medical_record_id', id);
      await api.post(API_ENDPOINTS.UPLOADS.UPLOAD, formData);
      await fetchAttachments();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setUploadError(msg || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <ImageIcon fontSize="small" />;
    return <DocIcon fontSize="small" />;
  };

  const fetchRecord = async () => {
    try {
      setLoading(true);
      const res = await api.get(API_ENDPOINTS.MEDICAL_RECORDS.GET(id!));
      setRecord(res.data || res);
      const data = res.data || res;
      setEditForm({
        complaint: data.complaint || '',
        symptoms: data.symptoms || '',
        diagnosis: data.diagnosis || '',
        physical_exam_findings: data.physical_exam_findings || '',
        treatment_plan: data.treatment_plan || '',
        prognosis: data.prognosis || '',
        notes: data.notes || '',
        status: data.status || '',
      });
    } catch (err) {
      setError('Failed to load medical record');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (!record) return;
    setEditForm({
      complaint: record.complaint || '',
      symptoms: record.symptoms || '',
      diagnosis: record.diagnosis || '',
      physical_exam_findings: record.physical_exam_findings || '',
      treatment_plan: record.treatment_plan || '',
      prognosis: record.prognosis || '',
      notes: record.notes || '',
      status: record.status || '',
    });
    setEditError('');
    setEditMode(true);
  };

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    setEditError('');
    try {
      const body: Record<string, string> = {};
      if (editForm.complaint !== record?.complaint) body.complaint = editForm.complaint;
      if (editForm.symptoms !== record?.symptoms) body.symptoms = editForm.symptoms;
      if (editForm.diagnosis !== record?.diagnosis) body.diagnosis = editForm.diagnosis;
      if (editForm.physical_exam_findings !== record?.physical_exam_findings) body.physical_exam_findings = editForm.physical_exam_findings;
      if (editForm.treatment_plan !== record?.treatment_plan) body.treatment_plan = editForm.treatment_plan;
      if (editForm.prognosis !== record?.prognosis) body.prognosis = editForm.prognosis;
      if (editForm.notes !== record?.notes) body.notes = editForm.notes;
      if (editForm.status !== record?.status) body.status = editForm.status;
      
      await api.put(API_ENDPOINTS.MEDICAL_RECORDS.UPDATE(id), body);
      setEditMode(false);
      await fetchRecord();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setEditError(msg || 'Failed to update medical record.');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'final':
      case 'documented': return 'success';
      case 'draft': return 'warning';
      case 'locked': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!record) {
    return (
      <Box>
        <Alert severity="error">{error || 'Medical record not found'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/medical-records')} sx={{ mt: 2 }}>
          Back to Medical Records
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/medical-records')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4">
            Medical Record #{record.id}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Patient: {record.pet_name} — {new Date(record.visit_date).toLocaleDateString()}
          </Typography>
        </Box>
        <Chip label={record.status} color={getStatusColor(record.status) as 'success' | 'warning' | 'error' | 'default'} size="small" sx={{ mr: 1 }} />
        {record.status !== 'locked' && (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
            Edit
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Patient & Visit Info</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Patient</Typography>
              <Button size="small" onClick={() => navigate(`/patients/${record.pet_id}`)} sx={{ p: 0 }}>
                {record.pet_name}
              </Button>
            </Box>
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Owner</Typography>
              <Typography>{record.owner_name || '—'}</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Veterinarian</Typography>
              <Typography>{record.veterinarian_name || '—'}</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Visit Date</Typography>
              <Typography>{new Date(record.visit_date).toLocaleDateString()}</Typography>
            </Box>
            {record.appointment_id && (
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">Appointment ID</Typography>
                <Typography>#{record.appointment_id}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Clinical Information</Typography>
            <Divider sx={{ mb: 2 }} />

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Chief Complaint</Typography>
              <Typography>{record.complaint || '—'}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Symptoms</Typography>
              <Typography>{record.symptoms || '—'}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Physical Exam Findings</Typography>
              <Typography>{record.physical_exam_findings || '—'}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Diagnosis</Typography>
              <Typography fontWeight="bold" color="primary.main">{record.diagnosis || '—'}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Treatment Plan</Typography>
              <Typography>{record.treatment_plan || '—'}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Prognosis</Typography>
              <Typography>{record.prognosis || '—'}</Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
              <Typography>{record.notes || '—'}</Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>SOAP Note</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box mb={3}>
              <Typography variant="subtitle2" color="secondary.main" gutterBottom><strong>S</strong> — Subjective</Typography>
              <Typography sx={{ pl: 2 }}>{record.complaint || '—'}</Typography>
              {record.symptoms && (
                <Typography sx={{ pl: 2, mt: 1 }} color="text.secondary">
                  Symptoms: {record.symptoms}
                </Typography>
              )}
            </Box>

            <Box mb={3}>
              <Typography variant="subtitle2" color="secondary.main" gutterBottom><strong>O</strong> — Objective</Typography>
              <Typography sx={{ pl: 2 }}>{record.physical_exam_findings || '—'}</Typography>
            </Box>

            <Box mb={3}>
              <Typography variant="subtitle2" color="secondary.main" gutterBottom><strong>A</strong> — Assessment</Typography>
              <Typography sx={{ pl: 2 }} fontWeight="bold">{record.diagnosis || '—'}</Typography>
              {record.prognosis && (
                <Typography sx={{ pl: 2, mt: 1 }} color="text.secondary">
                  Prognosis: {record.prognosis}
                </Typography>
              )}
            </Box>

            <Box mb={3}>
              <Typography variant="subtitle2" color="secondary.main" gutterBottom><strong>P</strong> — Plan</Typography>
              <Typography sx={{ pl: 2 }}>{record.treatment_plan || '—'}</Typography>
              {record.notes && (
                <Typography sx={{ pl: 2, mt: 1 }} color="text.secondary">
                  Notes: {record.notes}
                </Typography>
              )}
            </Box>

            {record.notebook_entry && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Consultation Notes</Typography>
                {record.notebook_entry.consultation_duration_minutes && (
                  <Typography variant="body2" color="textSecondary" mb={1}>
                    Duration: {record.notebook_entry.consultation_duration_minutes} minutes
                  </Typography>
                )}
                {record.notebook_entry.follow_up_instructions && (
                  <Box mb={2}>
                    <Typography variant="subtitle2">Follow-up Instructions</Typography>
                    <Typography>{record.notebook_entry.follow_up_instructions}</Typography>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          {record.prescriptions && record.prescriptions.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Prescriptions</Typography>
              <Divider sx={{ mb: 2 }} />
              {record.prescriptions.map((rx) => (
                <Box key={rx.id} mb={2} p={1.5} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle2">{rx.medication_name || `#${rx.id}`}</Typography>
                  {rx.prescription_number && (
                    <Typography variant="caption" color="textSecondary">{rx.prescription_number}</Typography>
                  )}
                  {rx.dosage && <Typography variant="body2">Dose: {rx.dosage}</Typography>}
                  {rx.quantity && <Typography variant="body2">Qty: {rx.quantity}</Typography>}
                  <Box mt={0.5}>
                    <Chip label={rx.status} size="small" />
                  </Box>
                </Box>
              ))}
            </Paper>
          )}

          <Paper sx={{ p: 3, mt: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6">Attachments</Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                Upload
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.dicom"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
            </Box>
            {uploadError && <Alert severity="error" sx={{ mb: 1 }}>{uploadError}</Alert>}
            {uploading && (
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="textSecondary">Uploading...</Typography>
              </Box>
            )}
            <Divider sx={{ mb: 1 }} />
            {attachments.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No attachments.</Typography>
            ) : (
              <List dense disablePadding>
                {attachments.map((att) => (
                  <ListItem
                    key={att.id}
                    disableGutters
                    sx={{ px: 0, py: 0.5 }}
                    secondaryAction={
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={() => window.open(att.r2_url, '_blank')}
                      >
                        <AttachmentIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {getFileIcon(att.file_type)}
                          <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                            {att.file_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ({formatFileSize(att.file_size)})
                          </Typography>
                        </Box>
                      }
                      secondary={att.description || new Date(att.created_at).toLocaleDateString()}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog open={editMode} onClose={() => setEditMode(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Medical Record #{record.id}</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Chief Complaint *"
              multiline rows={2}
              fullWidth value={editForm.complaint}
              onChange={(e) => setEditForm(f => ({ ...f, complaint: e.target.value }))}
            />
            <TextField
              label="Symptoms"
              multiline rows={2}
              fullWidth value={editForm.symptoms}
              onChange={(e) => setEditForm(f => ({ ...f, symptoms: e.target.value }))}
            />
            <TextField
              label="Physical Exam Findings"
              multiline rows={2}
              fullWidth value={editForm.physical_exam_findings}
              onChange={(e) => setEditForm(f => ({ ...f, physical_exam_findings: e.target.value }))}
            />
            <TextField
              label="Diagnosis"
              fullWidth value={editForm.diagnosis}
              onChange={(e) => setEditForm(f => ({ ...f, diagnosis: e.target.value }))}
            />
            <TextField
              label="Treatment Plan"
              multiline rows={2}
              fullWidth value={editForm.treatment_plan}
              onChange={(e) => setEditForm(f => ({ ...f, treatment_plan: e.target.value }))}
            />
            <TextField
              label="Prognosis"
              fullWidth value={editForm.prognosis}
              onChange={(e) => setEditForm(f => ({ ...f, prognosis: e.target.value }))}
            />
            <TextField
              label="Notes"
              multiline rows={2}
              fullWidth value={editForm.notes}
              onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))}
            />
            <TextField
              select
              label="Status"
              fullWidth
              value={editForm.status}
              onChange={(e) => setEditForm(f => ({ ...f, status: e.target.value }))}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="final">Final</MenuItem>
              <MenuItem value="locked">Locked</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMode(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicalRecordDetailPage;
